/**
 * Admin Memorials Tab
 * Manage memorial submissions
 */
import { useState, useEffect } from "react";
import { 
  Flame, Check, X, Trash2, Filter, Loader2, Cat, User, Calendar
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700"
};

const AdminMemorialsTab = () => {
  const { user } = useAuth();
  const [memorials, setMemorials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchMemorials();
    fetchStats();
  }, [filter]);

  const fetchMemorials = async () => {
    try {
      const url = filter === "all"
        ? `${API_URL}/api/memorials/all`
        : `${API_URL}/api/memorials/all?status=${filter}`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      const data = await res.json();
      setMemorials(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch memorials:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/memorials/stats/overview`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handleApprove = async (memorialId) => {
    try {
      await fetch(`${API_URL}/api/memorials/${memorialId}/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: "approved" })
      });
      fetchMemorials();
      fetchStats();
      setShowModal(false);
    } catch (err) {
      console.error("Failed to approve memorial:", err);
    }
  };

  const handleReject = async (memorialId) => {
    try {
      await fetch(`${API_URL}/api/memorials/${memorialId}/approve`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: "rejected" })
      });
      fetchMemorials();
      fetchStats();
      setShowModal(false);
    } catch (err) {
      console.error("Failed to reject memorial:", err);
    }
  };

  const handleDelete = async (memorialId) => {
    if (!window.confirm("Delete this memorial permanently?")) return;
    
    try {
      await fetch(`${API_URL}/api/memorials/${memorialId}`, {
        method: "DELETE",
        credentials: 'include'
      });
      setShowModal(false);
      fetchMemorials();
      fetchStats();
    } catch (err) {
      console.error("Failed to delete memorial:", err);
    }
  };

  const openMemorialDetail = (memorial) => {
    setSelectedMemorial(memorial);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-memorials-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-stone-900">{stats.total || 0}</p>
            <p className="text-sm text-stone-500">Total</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-700">{stats.pending || 0}</p>
            <p className="text-sm text-yellow-600">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
            <p className="text-sm text-stone-500">Approved</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected || 0}</p>
            <p className="text-sm text-stone-500">Rejected</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.catpals_cats || 0}</p>
            <p className="text-sm text-stone-500">CatPals+ Cats</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-500">{stats.total_candles || 0}</p>
            <p className="text-sm text-stone-500">Candles Lit</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-stone-500" />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="pending">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All Memorials</option>
        </select>
        {filter === "pending" && stats.pending > 0 && (
          <Badge className="bg-yellow-500 text-white">{stats.pending} awaiting review</Badge>
        )}
      </div>

      {/* Memorials Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-stone-400" />
        </div>
      ) : memorials.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Flame className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-semibold text-stone-700 mb-2">No memorials found</h3>
            <p className="text-stone-500">
              {filter === "pending" 
                ? "No memorials awaiting review"
                : "No memorials match this filter"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {memorials.map(memorial => (
            <Card 
              key={memorial.memorial_id} 
              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => openMemorialDetail(memorial)}
            >
              <div className="aspect-square relative">
                <img
                  src={`data:${memorial.image_mime_type};base64,${memorial.image_base64}`}
                  alt={memorial.cat_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2">
                  <Badge className={STATUS_COLORS[memorial.status]}>
                    {memorial.status}
                  </Badge>
                </div>
                {memorial.was_catpals_cat && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-amber-600 text-white">
                      <Cat className="w-3 h-3" />
                    </Badge>
                  </div>
                )}
                <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded flex items-center gap-1">
                  <Flame className="w-4 h-4 text-amber-400" />
                  <span className="text-sm">{memorial.candle_count || 0}</span>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="font-semibold text-stone-800 truncate">{memorial.cat_name}</p>
                <p className="text-sm text-stone-500 truncate">by {memorial.owner_name}</p>
                {memorial.status === "pending" && (
                  <div className="flex gap-2 mt-2" onClick={e => e.stopPropagation()}>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(memorial.memorial_id)}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(memorial.memorial_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Memorial Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Memorial Details</DialogTitle>
          </DialogHeader>
          
          {selectedMemorial && (
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden bg-stone-100">
                <img
                  src={`data:${selectedMemorial.image_mime_type};base64,${selectedMemorial.image_base64}`}
                  alt={selectedMemorial.cat_name}
                  className="w-full max-h-[300px] object-contain"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[selectedMemorial.status]}>
                    {selectedMemorial.status}
                  </Badge>
                  {selectedMemorial.was_catpals_cat && (
                    <Badge className="bg-amber-100 text-amber-700">
                      <Cat className="w-3 h-3 mr-1" />
                      CatPals+ Cat
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-amber-600">
                  <Flame className="w-5 h-5" />
                  <span className="font-medium">{selectedMemorial.candle_count || 0} candles</span>
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-2xl font-semibold text-stone-800">{selectedMemorial.cat_name}</h3>
                {(selectedMemorial.birth_year || selectedMemorial.passing_year) && (
                  <p className="text-stone-500">
                    {selectedMemorial.birth_year && `${selectedMemorial.birth_year}`}
                    {selectedMemorial.birth_year && selectedMemorial.passing_year && " - "}
                    {selectedMemorial.passing_year && `${selectedMemorial.passing_year}`}
                  </p>
                )}
              </div>

              <blockquote className="text-stone-600 italic p-4 bg-stone-50 rounded-lg">
                "{selectedMemorial.tribute_message}"
              </blockquote>

              <div className="bg-stone-50 rounded-lg p-4">
                <h4 className="font-medium text-stone-700 mb-2">Submitted by</h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <User className="w-4 h-4 text-stone-400" />
                    {selectedMemorial.owner_name}
                  </p>
                  {selectedMemorial.owner_email && (
                    <p className="flex items-center gap-2">
                      <span className="w-4 h-4 text-stone-400">@</span>
                      {selectedMemorial.owner_email}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-stone-400" />
                    {formatDate(selectedMemorial.created_at)}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedMemorial.status === "pending" && (
                  <>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedMemorial.memorial_id)}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(selectedMemorial.memorial_id)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
                <Button 
                  variant="outline" 
                  className="text-red-600 ml-auto"
                  onClick={() => handleDelete(selectedMemorial.memorial_id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMemorialsTab;
